/**
 * NgRX Entity Adapter for Pools Menu
 * 
 * Manages the normalized pool dictionary with built-in CRUD operations
 * and type-safe selectors.
 */

import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { PoolsMenuScope } from './pool.models';

/**
 * Entity adapter for Pool objects
 * Provides methods for:
 * - setAll, addOne, addMany
 * - updateOne, updateMany
 * - removeOne, removeMany
 * - upsertOne, upsertMany
 */
export const poolMenuAdapter: EntityAdapter<PoolsMenuScope.Pool> = createEntityAdapter<PoolsMenuScope.Pool>({
  selectId: (pool: PoolsMenuScope.Pool) => pool.id,
  sortComparer: false // Pools are accessed by ID, not sorted list
});

/**
 * Extended state interface for pools menu
 * Extends EntityState to include:
 * - ids: number[] - Array of pool IDs
 * - entities: { [id: number]: Pool } - Normalized pool dictionary
 */
export interface PoolMenuEntityState extends EntityState<PoolsMenuScope.Pool> {
  // Additional non-collection state
  selectedPoolId: number | null;
  isLoading: boolean;
  loaded: boolean;
}

/**
 * Initial state with entity adapter
 */
export const initialPoolMenuState: PoolMenuEntityState = poolMenuAdapter.getInitialState({
  selectedPoolId: null,
  isLoading: false,
  loaded: false
});

/**
 * Entity selectors - provided by adapter
 * These give us:
 * - selectIds - Select array of IDs
 * - selectEntities - Select normalized dictionary
 * - selectAll - Select array of all pools
 * - selectTotal - Select count of pools
 */
export const {
  selectIds: selectPoolIds,
  selectEntities: selectPoolEntities,
  selectAll: selectAllPools,
  selectTotal: selectTotalPools
} = poolMenuAdapter.getSelectors();
